import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
// Use dataset-specific editors instead of the generic JsonEditor
import HomeDataEditor from '../components/editors/HomeDataEditor';
import ServiceTypeDataEditor from '../components/editors/ServiceTypeDataEditor';
import NewsDataEditor from '../components/editors/NewsDataEditor';
import CasesDataEditor from '../components/editors/CasesDataEditor';
import CompanyDataEditor from '../components/editors/CompanyDataEditor';
import AdminDataEditor from '../components/editors/AdminDataEditor';

const AdminEditor = () => {
  const { isAuthenticated, user } = useAuth();
  const [activeKey, setActiveKey] = useState('home');

  // restore last selected tab for convenience
  useEffect(() => {
    try {
      const saved = localStorage.getItem('adminActiveTab');
      if (saved) setActiveKey(saved);
    } catch (_) {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('adminActiveTab', activeKey);
    } catch (_) {}
  }, [activeKey]);

  const tabs = useMemo(() => {
    const base = [
      { key: 'home', label: '首頁', heading: '首頁數據', filename: 'homeData.json' },
      { key: 'serviceTypes', label: '服務類型', heading: '服務類型', filename: 'serviceTypeData.json' },
      { key: 'news', label: '新聞', heading: '新聞列表', filename: 'newsData.json' },
      { key: 'cases', label: '案例', heading: '案例列表', filename: 'casesData.json' },
      { key: 'company', label: '公司', heading: '公司資訊', filename: 'companyData.json' },
    ];
    const isSuper = (user?.role || '') === 's';
    if (isSuper) {
      base.push({ key: 'admin', label: '管理員', heading: '管理員帳戶', filename: 'adminData.json' });
    }
    return base;
  }, [user]);

  // If current tab becomes unavailable (e.g., not super admin), reset to home
  useEffect(() => {
    if (!tabs.some(t => t.key === activeKey)) {
      setActiveKey('home');
    }
  }, [tabs, activeKey]);

  const active = tabs.find(t => t.key === activeKey) || tabs[0];

  // Company editor encapsulates its own schema hints; Editors handle their own data loading and saving

  if (!isAuthenticated) {
    return <Navigate to={`/login?redirect=/admin`} replace />;
  }

  return (
    <div className="container my-4">
      <h2 className="mb-3">管理員：網站數據編輯器</h2>

      {/* Tab list */}
      <ul className="nav nav-tabs mb-3" role="tablist" aria-label="數據類型">
        {tabs.map(tab => (
          <li className="nav-item" role="presentation" key={tab.key}>
            <button
              type="button"
              className={`nav-link ${active.key === tab.key ? 'active' : ''}`}
              role="tab"
              aria-selected={active.key === tab.key}
              aria-controls={`panel-${tab.key}`}
              id={`tab-${tab.key}`}
              onClick={() => setActiveKey(tab.key)}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      {/* Active panel */}
      <div
        id={`panel-${active.key}`}
        role="tabpanel"
        aria-labelledby={`tab-${active.key}`}
        className="mb-4"
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">{active.heading}</h4>
        </div>

        {active.key === 'home' && (<HomeDataEditor filename={active.filename} />)}
        {active.key === 'serviceTypes' && (<ServiceTypeDataEditor filename={active.filename} />)}
        {active.key === 'news' && (<NewsDataEditor filename={active.filename} />)}
        {active.key === 'cases' && (<CasesDataEditor filename={active.filename} />)}
        {active.key === 'company' && (<CompanyDataEditor filename={active.filename} />)}
        {active.key === 'admin' && (<AdminDataEditor filename={active.filename} />)}
      </div>
    </div>
  );
};

export default AdminEditor;
